// Overview — the team panels. Every figure links through to the screen that
// acts on it. Day-basis only: points would cost one query per consultant.
import { useMemo } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { T } from "./tokens";
import { Panel } from "./ui";
import { dayCellsFor, useVisitPool, useActiveConsultants, toISODate, addDays, sameDay } from "./data";
import type { WeeklyVisitSummary, VisitConsultantT } from "./data";

/** A number that goes somewhere. The whole point of the page. */
const Jump = ({
    value,
    label,
    sub,
    colour,
    onClick,
}: {
    value: string | number;
    label: string;
    sub?: string;
    colour?: string;
    onClick?: () => void;
}) => (
    <Box
        component={onClick ? "button" : "div"}
        onClick={onClick}
        sx={{
            border: `1px solid ${T.borderSoft}`,
            background: T.rowTint,
            borderRadius: "10px",
            padding: "12px 14px",
            textAlign: "left",
            fontFamily: T.font,
            cursor: onClick ? "pointer" : "default",
            display: "block",
            width: "100%",
            "&:hover": onClick ? { borderColor: T.purple, background: T.purpleTint } : undefined,
        }}
    >
        <Typography sx={{ fontSize: "24px", fontWeight: 800, color: colour || T.ink2, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>
            {value}
        </Typography>
        <Typography sx={{ fontSize: "12px", fontWeight: 700, color: T.ink2, mt: "3px" }}>{label}</Typography>
        {sub && <Typography sx={{ fontSize: "11px", color: T.muted, mt: "1px" }}>{sub}</Typography>}
    </Box>
);

const Row = ({ name, right, onClick }: { name: string; right: string; onClick?: () => void }) => (
    <Box
        onClick={onClick}
        sx={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
            padding: "7px 2px",
            borderTop: `1px solid ${T.borderSoft}`,
            cursor: onClick ? "pointer" : "default",
            "&:hover": onClick ? { background: T.rowTint } : undefined,
        }}
    >
        <Typography sx={{ fontSize: "12.5px", color: T.ink2, flexGrow: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {name}
        </Typography>
        <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: T.muted, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>
            {right}
        </Typography>
    </Box>
);

const Link = ({ label, onClick }: { label: string; onClick: () => void }) => (
    <Box
        component="button"
        onClick={onClick}
        sx={{
            border: "none",
            background: "transparent",
            color: T.purpleDeep,
            fontSize: "11.5px",
            fontWeight: 700,
            cursor: "pointer",
            fontFamily: T.font,
            padding: "10px 0 0",
            display: "block",
        }}
    >
        {label} →
    </Box>
);

interface Nav {
    /** Jump to a screen, optionally with its own filter already applied. */
    onGoSchedule: (gapsOnly?: boolean) => void;
    onGoConsultants: (nothingBookedOnly?: boolean) => void;
    onGoPool: () => void;
    onGoReports: () => void;
}

/**
 * Free working days and empty diaries across the visible team.
 *
 * A day only counts as free when the summary actually covers it. Its window is
 * about four weeks, so in month view several weekdays fall outside it — and an
 * uncovered day is "not known", never "bookable". Counting those would invent
 * capacity, which is the same trap the schedule's gap count avoids.
 */
export const NeedsAttentionPanel = ({
    data,
    isLoading,
    isError,
    team,
    dates,
    nav,
}: {
    data: WeeklyVisitSummary | null | undefined;
    isLoading: boolean;
    isError: boolean;
    team: string;
    dates: Date[];
    nav: Nav;
}) => {
    const roster = useActiveConsultants();
    const pool = useVisitPool(toISODate(addDays(new Date(), -365)), toISODate(addDays(new Date(), 90)));

    const stats = useMemo(() => {
        const summaryByEmail = new Map<string, VisitConsultantT>();
        (data?.regions || []).forEach((r) =>
            (r.consultants || []).forEach((c) => {
                if (c.email) summaryByEmail.set(c.email.toLowerCase(), c);
            }),
        );

        const inTeam = roster.active.filter((c) => team === "AllTeams" || (c.team || "Unknown") === team);

        let freeDays = 0;
        let uncovered = 0;
        const withGaps: Array<{ name: string; gaps: number }> = [];
        const nothingBooked: string[] = [];

        inTeam.forEach((rc) => {
            const c = summaryByEmail.get((rc.email || "").toLowerCase());
            if (!c) {
                nothingBooked.push(rc.name);
                return;
            }
            let gaps = 0;
            dates.forEach((d) => {
                const cell = dayCellsFor(c, d);
                if (!cell) {
                    uncovered += 1;
                    return;
                }
                if (cell.comment) return; // leave, admin, training
                if ((cell.bookingCount ?? 0) === 0) gaps += 1;
            });
            freeDays += gaps;
            if (gaps > 0) withGaps.push({ name: rc.name, gaps });
        });

        const today = new Date();
        const owed = (pool.data ?? []).filter((v) => {
            if (team === "AllTeams") return true;
            const rc = roster.active.find(
                (x) => (x.email || "").toLowerCase() === (v.allocatedToEmail || "").toLowerCase(),
            );
            return !!rc && (rc.team || "Unknown") === team;
        });
        const overdue = owed.filter((v) => v.dueDate && new Date(v.dueDate) < today).length;

        return {
            consultants: inTeam.length,
            freeDays,
            uncovered,
            withGaps: withGaps.sort((a, b) => b.gaps - a.gaps),
            nothingBooked: nothingBooked.sort(),
            overdue,
            poolLoaded: !!pool.data,
        };
    }, [data, roster.active, team, dates, pool.data]);

    if (isLoading)
        return (
            <Panel title="Needs attention" sx={{ height: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "center", py: "24px" }}><CircularProgress size={20} /></Box>
            </Panel>
        );
    if (isError)
        return (
            <Panel title="Needs attention" sx={{ height: "100%" }}>
                <Typography sx={{ fontSize: "12.5px", color: T.red }}>
                    ⚠ Couldn&apos;t load the schedule, so nothing here is a zero.
                </Typography>
            </Panel>
        );
    // The summary is gated on the H&S Admin flag and returns null without it. The
    // roster still loads, so counting would file everyone as "nothing booked".
    if (!data)
        return (
            <Panel title="Needs attention" sx={{ height: "100%" }}>
                <Typography sx={{ fontSize: "12.5px", color: T.muted, lineHeight: 1.6 }}>
                    Team figures need the <b>H&amp;S Admin</b> flag on your Salesforce employee record.
                </Typography>
            </Panel>
        );

    return (
        <Panel
            title="Needs attention"
            sub={`${stats.consultants} consultant${stats.consultants === 1 ? "" : "s"}${team === "AllTeams" ? "" : ` · ${team}`} · ${dates.length} weekdays`}
            sx={{ height: "100%" }}
        >
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(128px, 1fr))", gap: "10px" }}>
                <Jump
                    value={stats.freeDays}
                    label="Free days"
                    sub="working days with nothing booked"
                    colour={stats.freeDays > 0 ? T.amber : undefined}
                    onClick={() => nav.onGoSchedule(true)}
                />
                <Jump
                    value={stats.nothingBooked.length}
                    label="Empty diaries"
                    sub="no bookings at all"
                    colour={stats.nothingBooked.length > 0 ? T.red : undefined}
                    onClick={() => nav.onGoConsultants(true)}
                />
                <Jump
                    value={stats.poolLoaded ? stats.overdue : "…"}
                    label="Overdue work"
                    sub="past its due date, unbooked"
                    colour={stats.overdue > 0 ? T.red : undefined}
                    onClick={nav.onGoPool}
                />
            </Box>

            {stats.withGaps.length > 0 && (
                <Box sx={{ mt: "14px" }}>
                    <Typography sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: T.muted, mb: "2px" }}>
                        Most free days
                    </Typography>
                    {stats.withGaps.slice(0, 5).map((r) => (
                        <Row key={r.name} name={r.name} right={`${r.gaps} free`} onClick={() => nav.onGoSchedule(true)} />
                    ))}
                </Box>
            )}

            {stats.nothingBooked.length > 0 && (
                <Box sx={{ mt: "14px" }}>
                    <Typography sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: T.amber, mb: "2px" }}>
                        Nothing booked
                    </Typography>
                    {stats.nothingBooked.slice(0, 5).map((n) => (
                        <Row key={n} name={n} right="—" onClick={() => nav.onGoConsultants(true)} />
                    ))}
                    {stats.nothingBooked.length > 5 && (
                        <Link label={`See all ${stats.nothingBooked.length}`} onClick={() => nav.onGoConsultants(true)} />
                    )}
                </Box>
            )}

            {stats.uncovered > 0 && (
                <Typography sx={{ fontSize: "10.5px", color: T.muted2, mt: "12px" }}>
                    {stats.uncovered} weekdays sit outside the summary&apos;s ~4-week window and are not counted as
                    free — leave is not known for them.
                </Typography>
            )}
        </Panel>
    );
};

/**
 * Work owed but not booked. The overdue backlog dwarfs the forward book —
 * roughly ten to one — and that fact belongs on the front page rather than
 * inside the pool screen.
 */
export const OwedWorkPanel = ({ team, nav }: { team: string; nav: Nav }) => {
    const roster = useActiveConsultants();
    const pool = useVisitPool(toISODate(addDays(new Date(), -365)), toISODate(addDays(new Date(), 90)));

    const stats = useMemo(() => {
        const today = new Date();
        const teamOf = new Map(
            roster.active.map((c) => [(c.email || "").toLowerCase(), c.team || "No team"]),
        );

        const owed = (pool.data ?? []).filter(
            (v) => team === "AllTeams" || teamOf.get((v.allocatedToEmail || "").toLowerCase()) === team,
        );

        const overdue = owed.filter((v) => v.dueDate && new Date(v.dueDate) < today);
        const next90 = owed.filter((v) => v.dueDate && new Date(v.dueDate) >= today);
        const unallocated = owed.filter((v) => !v.allocatedToEmail);

        const byTeam = new Map<string, number>();
        owed.forEach((v) => {
            const t = v.allocatedToEmail ? teamOf.get((v.allocatedToEmail || "").toLowerCase()) || "No team" : "Unallocated";
            byTeam.set(t, (byTeam.get(t) || 0) + 1);
        });

        const soonest = next90
            .map((v) => v.dueDate)
            .filter(Boolean)
            .sort()[0];

        return {
            total: owed.length,
            overdue: overdue.length,
            next90: next90.length,
            unallocated: unallocated.length,
            byTeam: [...byTeam.entries()].sort((a, b) => b[1] - a[1]),
            soonest,
        };
    }, [pool.data, roster.active, team]);

    if (pool.isLoading)
        return (
            <Panel title="Owed work" sx={{ height: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "center", py: "24px" }}><CircularProgress size={20} /></Box>
            </Panel>
        );
    if (pool.isError)
        return (
            <Panel title="Owed work" sx={{ height: "100%" }}>
                <Typography sx={{ fontSize: "12.5px", color: T.red }}>
                    ⚠ Couldn&apos;t load the visit pool, so nothing here is a zero.
                </Typography>
            </Panel>
        );

    return (
        <Panel
            title="Owed work"
            sub="visits with a due date and no diary slot · a year back to 90 days ahead"
            sx={{ height: "100%" }}
        >
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(128px, 1fr))", gap: "10px" }}>
                <Jump
                    value={stats.overdue}
                    label="Overdue"
                    sub="already past due"
                    colour={stats.overdue > 0 ? T.red : undefined}
                    onClick={nav.onGoPool}
                />
                <Jump value={stats.next90} label="Due next 90 days" sub="still in time" onClick={nav.onGoPool} />
                <Jump
                    value={stats.unallocated}
                    label="Unallocated"
                    sub="no consultant assigned"
                    colour={stats.unallocated > 0 ? T.amber : undefined}
                    onClick={nav.onGoPool}
                />
            </Box>

            {stats.byTeam.length > 1 && (
                <Box sx={{ mt: "14px" }}>
                    <Typography sx={{ fontSize: "10.5px", fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: T.muted, mb: "2px" }}>
                        By team
                    </Typography>
                    {stats.byTeam.slice(0, 6).map(([name, n]) => (
                        <Row key={name} name={name} right={String(n)} onClick={nav.onGoPool} />
                    ))}
                </Box>
            )}

            {stats.soonest && (
                <Typography sx={{ fontSize: "11px", color: T.muted, mt: "12px" }}>
                    Next due{" "}
                    {new Date(stats.soonest).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}.
                </Typography>
            )}

            <Link label="Open the visit pool" onClick={nav.onGoPool} />
        </Panel>
    );
};

/**
 * Where the team sits over the summary's own window.
 *
 * Everything here is already in the weekly-summary payload, so it costs
 * nothing extra. It is all on the DAY basis, which is the long-standing
 * measure — points need one query per consultant, so this panel links to
 * Reports rather than paying that on the landing page.
 */
export const TeamPositionPanel = ({
    data,
    isLoading,
    isError,
    nav,
}: {
    data: WeeklyVisitSummary | null | undefined;
    isLoading: boolean;
    isError: boolean;
    nav: Nav;
}) => {
    if (isLoading)
        return (
            <Panel title="Team position" sx={{ height: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "center", py: "24px" }}><CircularProgress size={20} /></Box>
            </Panel>
        );
    if (isError || !data)
        return (
            <Panel title="Team position" sx={{ height: "100%" }}>
                <Typography sx={{ fontSize: "12.5px", color: T.muted }}>
                    {isError ? "⚠ Couldn't load the team summary." : "No team summary for your account."}
                </Typography>
            </Panel>
        );

    const avg = data.averageBookings;
    const prev = data.averageBookingsPrevPeriod;
    const delta = avg != null && prev != null ? Math.round((avg - prev) * 10) / 10 : null;

    return (
        <Panel
            title="Team position"
            sub="the weekly summary's own ~4-week window · day basis"
            sx={{ height: "100%" }}
        >
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(128px, 1fr))", gap: "10px" }}>
                <Jump value={data.bookingCount ?? "—"} label="Booked days" sub="across the team" onClick={nav.onGoReports} />
                <Jump
                    value={data.utilisation == null ? "—" : `${data.utilisation}%`}
                    label="Utilisation · days"
                    sub="booked against available"
                    colour={T.purple}
                    onClick={nav.onGoReports}
                />
                <Jump
                    value={avg == null ? "—" : avg}
                    label="Average per consultant"
                    sub={delta == null ? "bookings" : `${delta >= 0 ? "+" : ""}${delta} vs previous period`}
                    colour={delta == null ? undefined : delta >= 0 ? T.green : T.amber}
                    onClick={nav.onGoReports}
                />
            </Box>

            <Box sx={{ mt: "14px" }}>
                {data.highestBookingPerson && (
                    <Row name={`Most booked · ${data.highestBookingPerson}`} right={`${data.highestBookingCount ?? "—"} days`} />
                )}
                {data.lowestBookingPerson && (
                    <Row name={`Least booked · ${data.lowestBookingPerson}`} right={`${data.lowestBookingCount ?? "—"} days`} />
                )}
                {data.totalLostDays != null && (
                    <Row name="Leave, admin and training" right={`${data.totalLostDays} days`} />
                )}
            </Box>

            <Typography sx={{ fontSize: "10.5px", color: T.muted2, mt: "12px", lineHeight: 1.5 }}>
                Day basis: a half-day visit counts as a full day. Points price it as half — see Reports for the
                points measure and the per-consultant detail.
            </Typography>

            <Link label="Open reports" onClick={nav.onGoReports} />
        </Panel>
    );
};
